import type { CreatorShopApplicationSummary } from '../../services/adminApi'

interface ApplicationCardProps {
  application: CreatorShopApplicationSummary
  onClick?: (application: CreatorShopApplicationSummary) => void
}

const statusColors: Record<string, { bg: string; text: string; dot: string }> = {
  pending: { bg: 'bg-amber-50 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-300', dot: 'bg-amber-400' },
  approved: { bg: 'bg-emerald-50 dark:bg-emerald-900/30', text: 'text-emerald-700 dark:text-emerald-300', dot: 'bg-emerald-400' },
  rejected: { bg: 'bg-red-50 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-300', dot: 'bg-red-400' },
  waitlisted: { bg: 'bg-sky-50 dark:bg-sky-900/30', text: 'text-sky-700 dark:text-sky-300', dot: 'bg-sky-400' },
  canceled: { bg: 'bg-gray-200 dark:bg-gray-700', text: 'text-gray-500 dark:text-gray-400', dot: 'bg-gray-400' },
}

export function ApplicationCard({ application, onClick }: ApplicationCardProps) {
  const submittedAt = application.submitted_at
    ? new Date(application.submitted_at).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : '—'

  const status = statusColors[application.status] || statusColors.canceled
  const displayName = application.shop_name || application.desired_handle || 'Unnamed shop'
  const initials = displayName.charAt(0).toUpperCase()

  return (
    <div
      role="button"
      tabIndex={0}
      className="group relative rounded-2xl border border-[#ecdefc] dark:border-[#3d2c4f] bg-white dark:bg-[#1a1520] p-5 transition-all duration-300 cursor-pointer hover:shadow-md hover:-translate-y-1 shadow-sm dark:shadow-none focus:outline-none focus-visible:ring-2 focus-visible:ring-[#503864] dark:focus-visible:ring-[#b58fcc]"
      onClick={() => onClick?.(application)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onClick?.(application)
        }
      }}
    >
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 flex-shrink-0 rounded-xl bg-[#503864] dark:bg-[#7f4f9a] flex items-center justify-center">
            <span className="text-lg font-bold text-white">{initials}</span>
          </div>
          <div className="min-w-0">
            <h3 className="font-semibold text-gray-900 dark:text-gray-100 truncate group-hover:text-[#503864] dark:group-hover:text-[#b58fcc] transition-colors">
              {displayName}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
              {application.applicant_email || application.applicant_user_id}
            </p>
          </div>
        </div>
        <span className={`flex-shrink-0 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${status.bg} ${status.text}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${status.dot}`} />
          <span className="capitalize">{application.status}</span>
        </span>
      </div>

      <div className="grid grid-cols-2 gap-3 pt-4 border-t border-[#f0e3ff] dark:border-[#3d2c4f] text-xs text-gray-500 dark:text-gray-400">
        <div>
          <p className="font-medium text-gray-900 dark:text-gray-100">{submittedAt}</p>
          <p>Submitted</p>
        </div>
        <div>
          <p className="font-medium text-gray-900 dark:text-gray-100 font-mono truncate">
            {application.desired_handle || '—'}
          </p>
          <p>Desired handle</p>
        </div>
      </div>

      {application.decision_reason_category && (
        <div className="mt-3 text-xs text-gray-500 dark:text-gray-400">
          Reason: <span className="font-mono">{application.decision_reason_category}</span>
        </div>
      )}
    </div>
  )
}
